/**
 * Host path ↔ VFS path. The VFS path space is POSIX ('/'-rooted, '/'-separated) on every platform, so
 * the agent, its tools and every IFilesystem behave identically everywhere. On macOS/Linux a host
 * path IS a VFS path (root '/' = machine root) and both functions are the identity — byte-for-byte.
 *
 * Windows has drive letters and backslashes, so its host paths are mapped Git-Bash/MSYS style:
 *   C:\Users\x        ↔ /c/Users/x          (drive letter → first segment, lower-cased)
 *   \\?\C:\Users\x    → /c/Users/x          (the long-path prefix is dropped)
 *   \\server\share\x  ↔ /unc/server/share/x (also \\?\UNC\server\share\x)
 *   src\a.ts          → src/a.ts            ('\' is a separator there — it can't occur in a file name)
 * so a model (or user) can hand a tool `C:\…`, `C:/…` or `/c/…` and land on the same file, and `..`
 * written with backslashes is normalized by the VFS like any other `..` (no escape past a jail root).
 *
 * Pure string functions, no node imports (edge-safe); `platform` is injectable for tests.
 */

const currentPlatform = (): string => (typeof process !== 'undefined' && process.platform) || 'browser';

/** True when host paths need translating (Windows). */
export const isWinPaths = (platform: string = currentPlatform()): boolean => platform === 'win32';

/** A host path (or a VFS path — those pass through) → VFS form. Identity off Windows. */
export function toVfsPath(p: string, platform: string = currentPlatform()): string {
  if (!isWinPaths(platform) || typeof p !== 'string') return p;
  let s = p.replace(/\\/g, '/');
  s = s.replace(/^\/\/\?\/UNC\//i, '//').replace(/^\/\/\?\//, ''); // \\?\UNC\srv\share → //srv/share; \\?\C:\ → C:/
  const drive = /^([A-Za-z]):(?=\/|$)/.exec(s);
  if (drive) return `/${drive[1].toLowerCase()}${s.slice(2) || '/'}`.replace(/\/+$/, '') || '/';
  if (/^\/\/[^/]/.test(s)) return `/unc/${s.slice(2)}`;
  return s;
}

/** An ABSOLUTE, normalized VFS path → the host path it names on a machine-rooted disk; null when it
 *  names nothing on the host (Windows: '/', '/unc', '/unc/srv' or a first segment that isn't a drive). Identity off Windows. */
export function toHostPath(vpath: string, platform: string = currentPlatform()): string | null {
  if (!isWinPaths(platform)) return vpath;
  const segs = vpath.split('/').filter(Boolean);
  if (segs.length >= 1 && /^[A-Za-z]$/.test(segs[0])) return `${segs[0].toUpperCase()}:\\${segs.slice(1).join('\\')}`;
  if (segs.length >= 3 && segs[0].toLowerCase() === 'unc') return `\\\\${segs.slice(1).join('\\')}`;
  return null;
}

/** Wrap an IFilesystem so every path argument is normalized with `toVfsPath` first — for backends that
 *  know nothing of host paths (an in-memory sandbox copy, a bod-db store) when the host is Windows and
 *  callers (the CLI, a model) speak `C:\…`. Returns `fs` itself where there is nothing to translate,
 *  so off Windows this is a no-op. Optional capabilities (readFileBytes, realPath, canonicalPath) pass through. */
export function withHostPaths<T extends object>(fs: T, platform: string = currentPlatform()): T {
  if (!isWinPaths(platform)) return fs;
  const PATH_ARGS: Record<string, number> = {
    readFile: 1, writeFile: 1, deleteFile: 1, readDir: 1, createDir: 1, exists: 1, stat: 1, isDirectory: 1, isFile: 1,
    setCwd: 1, resolvePath: 2, readFileBytes: 1, realPath: 1, canonicalPath: 1,
  };
  return new Proxy(fs, {
    get(target, prop, receiver) {
      const v = Reflect.get(target, prop, receiver);
      const n = typeof prop === 'string' ? PATH_ARGS[prop] : undefined;
      if (typeof v !== 'function' || prop === 'constructor') return v; // `fs.constructor.name` stays the backend's
      if (!n) return v.bind(target);
      return (...args: unknown[]) => v.apply(target, args.map((a, i) => (i < n && typeof a === 'string' ? toVfsPath(a, platform) : a)));
    },
  });
}

/** Windows device names that open a device (console, printer, null…) instead of a file, in ANY
 *  directory and with any extension (`nul.txt` is still NUL). */
const WIN_DEVICE = /^(con|prn|aux|nul|com[0-9¹²³]|lpt[0-9¹²³]|conin\$|conout\$)(\..*)?$/i;

/** Why a single VFS path segment is unsafe to hand to the Windows file APIs, or null when it's fine.
 *  These are ALIASES — names that open a different file than the one they spell — so a name-based
 *  policy (the jail's `**\/.env` denylist) would check one name while the OS opens another:
 *   - ':' opens an NTFS alternate data stream (`.env::$DATA` reads `.env`) or is drive-relative;
 *   - a trailing '.' or ' ' is stripped by Win32 path normalization (`.env.` → `.env`);
 *   - a reserved device name opens the device (`CON` blocks on console input).
 *  8.3 short names (`ENV~1` → `.env`) need the disk to resolve; NodeDiskFilesystem checks those. */
export function winSegmentProblem(seg: string): string | null {
  if (seg.includes(':')) return 'alternate data stream / drive-relative name';
  if (/[. ]$/.test(seg) && seg !== '.' && seg !== '..') return 'trailing dot or space';
  if (WIN_DEVICE.test(seg)) return 'reserved device name';
  if (/[<>"|?*\x00-\x1f]/.test(seg)) return 'invalid character';
  return null;
}
